import React, { useState, useLayoutEffect, useCallback, useRef, useMemo, memo } from 'react';
import classnames from 'classnames';
import isFunction from 'lodash/isFunction';
import { toolbarConfig, TOOLBAR_BUTTONS_TAGS } from './react-quill-text-editor-toolbar-config';
import DropdownChevronDown from 'monday-ui-react-core/dist/icons/DropdownChevronDown';
import DropdownChevronUp from 'monday-ui-react-core/dist/icons/DropdownChevronUp';

import './react-quill-text-editor-toolbar-component.scss';

const TOOLBAR_BUTTON_DEFAULT_WIDTH = 30;
const TOOLBAR_WIDTH_OFFSET = 74 + TOOLBAR_BUTTON_DEFAULT_WIDTH;

function TextEditorToolbarComponent({ hidden, editorId }) {
  const toolbarRef = useRef();
  const [showHiddenMenu, setShowHiddenMenu] = useState(false);
  const [toolbarButtons] = useState(toolbarConfig);
  const [hiddenToolbarButtons, setHiddenToolbarButtons] = useState(0);

  const toolbarWidth = useMemo(() => {
    return toolbarButtons.reduce((sum, { width = TOOLBAR_BUTTON_DEFAULT_WIDTH }) => sum + width, 0);
  }, [toolbarButtons]);

  const handleWindowResize = useCallback(() => {
    if (!toolbarRef.current || toolbarRef.current.clientWidth <= 0) return;

    const tmpToolbar = [...toolbarButtons];
    const maxToolbarWidth = toolbarRef.current.clientWidth - TOOLBAR_WIDTH_OFFSET;

    let currentToolbarWidth = toolbarWidth;
    let tmpHiddenButtons = 0;

    // more than one line
    if (toolbarWidth > maxToolbarWidth) {
      for (let indx = toolbarButtons.length - 1; indx >= 0; indx--) {
        const button = tmpToolbar[indx];
        const { width = TOOLBAR_BUTTON_DEFAULT_WIDTH } = button;

        if (currentToolbarWidth - width > maxToolbarWidth) {
          currentToolbarWidth -= width;
          tmpHiddenButtons++;
        } else {
          break;
        }
      }
    }

    setHiddenToolbarButtons(tmpHiddenButtons);
  }, [toolbarWidth, toolbarRef, toolbarButtons]);

  useLayoutEffect(() => {
    if (hidden) return;

    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [hidden, handleWindowResize]);

  const renderToolbarTag = (toolbarButton, index) => {
    const { name, value, tag, options = [] } = toolbarButton;
    const isHidden = index > toolbarButtons.length - hiddenToolbarButtons && !showHiddenMenu;

    switch (tag) {
      case TOOLBAR_BUTTONS_TAGS.BUTTON: {
        return (
          <button
            className={classnames(`ql-${name} toolbar-button`, {
              hidden: isHidden,
            })}
            value={value}
            key={`toolbar_${name}_${value}_${editorId}`}
          />
        );
      }
      case TOOLBAR_BUTTONS_TAGS.SELECT: {
        if (!options) return null;

        const selectOptions = isFunction(options) ? options() : options;

        return (
          <select
            className={classnames(`ql-${name} toolbar-button`, {
              hidden: isHidden,
            })}
            defaultValue={''}
            onChange={(e) => e.persist()}
            value={value}
            key={`toolbar_${name}_${editorId}`}
          >
            {selectOptions.map((option) => (
              <option key={`${name}_${option}_${editorId}`} value={option}></option>
            ))}
          </select>
        );
      }

      default:
        return null;
    }
  };

  const renderHiddenButtonIfNeeded = () => {
    if (hiddenToolbarButtons < 2) return;

    return (
      <div className="text-editor-toolbar-hidden-menu-wrapper">
        <div
          className={classnames('hidden-menu-toggler', {
            'is-open': showHiddenMenu,
          })}
          onClick={() => setShowHiddenMenu(!showHiddenMenu)}
        >
          {!showHiddenMenu ? <DropdownChevronDown /> : <DropdownChevronUp />}
        </div>
      </div>
    );
  };

  const renderToolbar = () => {
    if (!toolbarButtons || toolbarButtons.length === 0) return;

    return (
      <div
        ref={toolbarRef}
        className={classnames('ql-toolbar ql-snow', 'react-quill-text-editor-toolbar-component', {
          hidden,
          'show-hidden': showHiddenMenu,
        })}
        id={`t-${editorId}`}
      >
        {toolbarButtons.map(renderToolbarTag)}
        {renderHiddenButtonIfNeeded()}
      </div>
    );
  };

  return renderToolbar();
}

export default memo(TextEditorToolbarComponent);
